import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck } from "lucide-react";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";

const PORTALS = {
  admin: {
    heading: "ACS Administrator",
    blurb: "Restricted access for Alabama Church School staff.",
    home: "/admin",
    other: null,
    image:
      "https://images.unsplash.com/photo-1568667256549-094345857637?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    caption: "Review submitted records, approve them, and issue the annual academic document.",
  },
  partner: {
    heading: "Partner sign in",
    blurb: "For educational partner staff submitting annual academic records.",
    home: "/partner",
    other: null,
    image:
      "https://images.unsplash.com/photo-1516321497487-e288fb19713f?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    caption: "Upload your students' annual academic records package for ACS review.",
  },
};

export default function Login({ portal = "partner" }) {
  const config = PORTALS[portal];
  const { user, login, logout } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user.role === portal) navigate(config.home, { replace: true });
  }, [user, portal, config.home, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const result = await login(email, password);
    setBusy(false);
    if (!result.ok) {
      setError(result.error);
      return;
    }
    if (result.user.role !== portal) {
      await logout();
      setError(
        portal === "admin"
          ? "This is the ACS administrator sign-in. Partner staff should use the partner sign-in page."
          : "This is the partner sign-in. ACS administrators should use the administrator sign-in page."
      );
      return;
    }
    navigate(config.home, { replace: true });
  };

  return (
    <div className="grid min-h-screen text-left lg:grid-cols-2">
      <div className="flex flex-col justify-center px-6 py-14 sm:px-14">
        <Link to="/" className="mb-10 inline-block">
          <Brand />
        </Link>
        <div className="max-w-sm">
          {portal === "admin" && (
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-100 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-600">
              <ShieldCheck className="h-3.5 w-3.5" /> Staff only
            </div>
          )}
          <h1 className="font-display text-3xl font-bold text-slate-900">{config.heading}</h1>
          <p className="mt-2 text-sm text-slate-600">{config.blurb}</p>
          {user && user.role !== portal && (
            <div
              className="mt-5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800"
              data-testid="wrong-portal-notice"
            >
              You are signed in as {user.email}, which does not have access here.{" "}
              <button
                className="font-semibold underline"
                onClick={async () => {
                  await logout();
                  setError("");
                }}
                data-testid="wrong-portal-signout"
              >
                Sign out
              </button>{" "}
              or go to the{" "}
              <Link
                to={user.role === "admin" ? "/admin" : "/partner"}
                className="font-semibold underline"
              >
                {user.role === "admin" ? "administrator" : "partner"} portal
              </Link>
              .
            </div>
          )}
          <Card className="mt-8 border-slate-200">
            <CardContent className="p-7">
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="login-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <Input
                    id="password"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    data-testid="login-password-input"
                  />
                </div>
                {error && (
                  <p className="text-sm text-red-600" data-testid="login-error">
                    {error}
                  </p>
                )}
                <Button
                  type="submit"
                  className="w-full gap-2"
                  disabled={busy}
                  data-testid="login-submit-button"
                >
                  {busy && <Loader2 className="h-4 w-4 animate-spin" />} Sign in
                </Button>
                <Link
                  to={`/forgot-password?portal=${portal}`}
                  className="block text-center text-xs text-blue-700 hover:underline"
                  data-testid="forgot-password-link"
                >
                  Forgot your password?
                </Link>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
      <div className="relative hidden lg:block">
        <img src={config.image} alt="" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-display text-3xl font-bold">Annual Academic Records</h2>
          <p className="mt-3 max-w-md text-sm text-slate-200">{config.caption}</p>
        </div>
      </div>
    </div>
  );
}
